import type { JSX } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LOGGED_OUT_PATH } from '@/auth/login-gate';
import { Layout } from '@/components/layout/layout';
import { Dashboard } from '@/pages/dashboard';
import { Users } from '@/pages/users';
import { UserDetail } from '@/pages/user-detail';
import { Segments } from '@/pages/segments';
import { GroupMappings } from '@/pages/group-mappings';
import { ModelProviders } from '@/pages/providers';
import { Models } from '@/pages/models';
import { Skills } from '@/pages/skills';
import { Mcps } from '@/pages/mcps';
import { McpTools } from '@/pages/mcp-tools';
import { KnowledgeBases } from '@/pages/knowledge-bases';
import { Bindings } from '@/pages/bindings';
import { Costs } from '@/pages/costs';
import { AuditLog } from '@/pages/audit-log';
import { Compliance } from '@/pages/compliance';
import { ErrorLogs } from '@/pages/error-logs';
import { Conversations } from '@/pages/conversations';
import { SystemSettingsPage } from '@/pages/system-settings';
import { SystemPromptPage } from '@/pages/system-prompt';
import { Downloads } from '@/pages/downloads';
import { EchoProxyPage } from '@/pages/echoproxy';
import { LoggedOut } from '@/pages/logged-out';
import { RequireRole } from '@/components/auth/require-role';
import { ForbiddenNotice } from '@/components/shared/forbidden-notice';
import { useAdminRoles } from '@/auth/admin-role-context';
import { homeRouteFor } from '@/auth/admin-role';
import { DESKTOP_DOWNLOADS, KNOWLEDGE_BASE, isFeatureEnabled } from '@/auth/features';

// The admin shell (sidebar + topbar) wraps every operator page. The logged-out screen is
// deliberately kept out of it — see @/pages/logged-out (EW-190).
function AdminShell(): JSX.Element {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

// The landing page depends on the role: ADMIN gets the dashboard, the others start on a page
// they can actually open, and a role-less user gets the forbidden notice instead of a 403 flash.
function RoleHome(): JSX.Element {
  const { isAdmin, roles, isPending } = useAdminRoles();
  if (isPending) {
    return <div className="card p-10 text-center text-sm text-muted-foreground">Betöltés…</div>;
  }
  if (isAdmin) return <Dashboard />;
  const home = homeRouteFor(roles);
  return home === '/' ? <ForbiddenNotice /> : <Navigate to={home} replace />;
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path={LOGGED_OUT_PATH} element={<LoggedOut />} />
        <Route element={<AdminShell />}>
          <Route path="/" element={<RoleHome />} />
          <Route element={<RequireRole roles={['ADMIN']} />}>
            <Route path="/users" element={<Users />} />
            <Route path="/users/:id" element={<UserDetail />} />
            <Route path="/segments" element={<Segments />} />
            <Route path="/group-mappings" element={<GroupMappings />} />
            <Route path="/costs" element={<Costs />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/settings" element={<SystemSettingsPage />} />
            {isFeatureEnabled(DESKTOP_DOWNLOADS) && <Route path="/downloads" element={<Downloads />} />}
            <Route path="/echoproxy" element={<EchoProxyPage />} />
          </Route>
          <Route element={<RequireRole roles={['ADMIN', 'AI_ADMIN']} />}>
            <Route path="/providers" element={<ModelProviders />} />
            <Route path="/models" element={<Models />} />
            <Route path="/skills" element={<Skills />} />
            <Route path="/mcps" element={<Mcps />} />
            <Route path="/mcps/:id/tools" element={<McpTools />} />
            {isFeatureEnabled(KNOWLEDGE_BASE) && (
              <Route path="/knowledge-bases" element={<KnowledgeBases />} />
            )}
            <Route path="/system-prompt" element={<SystemPromptPage />} />
            <Route path="/bindings" element={<Bindings />} />
          </Route>
          <Route element={<RequireRole roles={['ADMIN', 'AUDITOR']} />}>
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/error-logs" element={<ErrorLogs />} />
            <Route path="/conversations" element={<Conversations />} />
          </Route>
          <Route path="/auth/callback" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
